import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PeopleStats, Site, SiteList, Tenant } from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardPad,
  Drawer,
  Empty,
  Field,
  Form,
  IconBtn,
  Input,
  Note,
  PageHead,
  Row,
  Select,
  Table,
  useAdminToast,
} from '../admin-kit';
import { useSession } from '../../shared/session';
import { ListError } from '../../shared/list-error';

/**
 * De vestigingen van het eigen bedrijf (Stap 29, ADR-0073; ontwerp O3).
 *
 * Eén tenant met sites in plaats van gekoppelde bedrijven: zo is "alle vestigingen" de standaard
 * en "alleen Kortrijk" de uitzondering. Dit scherm is waar een klant dat inricht.
 *
 * **Het aantal mensen komt van een tweede aanroep.** `GET /sites` weet niets van medewerkers -
 * `tenants` bezit de vestigingen en `directory` de mensen, en een module telt wat ze bezit
 * (ADR-0016). Het scherm legt de twee antwoorden naast elkaar.
 */
export function SitesPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  const adminToast = useAdminToast();

  const [nieuw, setNieuw] = useState(false);
  const [bewerk, setBewerk] = useState<Site | null>(null);

  const canManage = has('tenants.site.manage');

  const sites = useQuery({
    queryKey: ['sites', 'all'],
    queryFn: async (): Promise<Site[]> => {
      const { data, error } = await api.GET('/api/v1/sites', {
        params: { query: { includeClosed: true } } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as SiteList).items;
    },
  });

  const stats = useQuery({
    queryKey: ['people', 'stats'],
    enabled: has('directory.person.view'),
    queryFn: async (): Promise<PeopleStats> => {
      const { data, error } = await api.GET('/api/v1/people/stats', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PeopleStats;
    },
  });

  /*
    De tijdzone van het bedrijf, voor "Volgt het bedrijf (Europe/Brussels)".

    Dezelfde query als de schil en het medewerkersscherm, dus geen extra verkeer. Zolang ze er
    niet is, staat er "Volgt het bedrijf" zonder haakjes: dat klopt ook.
  */
  const tenant = useQuery({
    queryKey: ['tenant', 'self'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/tenant');
      return (data as Tenant | undefined) ?? null;
    },
  });
  const bedrijfszone = tenant.data?.timezone ?? null;
  const volgtBedrijf = bedrijfszone
    ? t('beheer.organisatie.sites.timezoneInherited', { zone: bedrijfszone })
    : t('sites.timezoneInherited');

  const perSite = stats.data?.perSite ?? {};

  const opgeslagen = async () => {
    setNieuw(false);
    setBewerk(null);
    await queryClient.invalidateQueries({ queryKey: ['sites'] });
    adminToast.show(t('sites.saved'));
  };

  return (
    <AdminPage>
      <PageHead
        title={t('sites.title')}
        sub={t('sites.intro')}
        actions={
          canManage ? (
            <Btn icon={<AdminIcon name="plus" />} testId="site-new" onClick={() => setNieuw(true)}>
              {t('sites.new')}
            </Btn>
          ) : null
        }
      />

      <Card>
        {sites.isPending ? (
          <CardPad>
            <LoadingState rows={3} label={t('common.loading')} />
          </CardPad>
        ) : sites.isError ? (
          <CardPad>
            <ListError onRetry={() => void sites.refetch()} error={sites.error} />
          </CardPad>
        ) : (sites.data?.length ?? 0) === 0 ? (
          <Empty
            icon={<AdminIcon name="building" size={24} />}
            title={t('sites.emptyTitle')}
            text={t('sites.emptyHint')}
          />
        ) : (
          <Table label={t('sites.title')}>
            <thead>
              <tr>
                <th>{t('sites.code')}</th>
                <th>{t('sites.name')}</th>
                <th>{t('sites.timezone')}</th>
                <th className="num">{t('sites.people')}</th>
                <th>{t('sites.status')}</th>
                <th className="act" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {(sites.data ?? []).map((site) => {
                const mensen = perSite[site.id] ?? 0;
                return (
                  <tr key={site.id} data-testid="site-row">
                    <td>
                      <span className="a-mono">{site.code}</span>
                    </td>
                    <td>
                      <div className="a-name">{site.name}</div>
                      {site.address ? <div className="a-meta">{site.address}</div> : null}
                    </td>
                    <td>
                      {/* Leeg is een verwijzing en geen ontbrekende waarde: volg het bedrijf. */}
                      {site.timezone ? (
                        site.timezone
                      ) : (
                        <span className="a-muted">{volgtBedrijf}</span>
                      )}
                    </td>
                    <td className="num">{mensen}</td>
                    <td>
                      {/*
                        "Nog leeg" alleen als we het weten: zonder het recht om mensen te zien,
                        is er geen telling, en dan is een open vestiging gewoon actief.
                      */}
                      {site.status === 'closed' ? (
                        <Badge>{t('sites.statusValue.closed')}</Badge>
                      ) : stats.data && mensen === 0 ? (
                        <Badge>{t('beheer.organisatie.sites.statusEmpty')}</Badge>
                      ) : (
                        <Badge tone="ok">{t('sites.statusValue.active')}</Badge>
                      )}
                    </td>
                    <ActionsCell>
                      {canManage ? (
                        <IconBtn
                          label={t('common.edit')}
                          icon={<AdminIcon name="pencil" />}
                          testId="site-edit"
                          onClick={() => setBewerk(site)}
                        />
                      ) : null}
                    </ActionsCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {nieuw ? (
        <SiteDrawer
          companyZoneLabel={volgtBedrijf}
          onClose={() => setNieuw(false)}
          onSaved={opgeslagen}
        />
      ) : null}

      {bewerk ? (
        <SiteDrawer
          site={bewerk}
          companyZoneLabel={volgtBedrijf}
          onClose={() => setBewerk(null)}
          onSaved={opgeslagen}
        />
      ) : null}

      {adminToast.element}
    </AdminPage>
  );
}

/** De IANA-tijdzones van de browser; een oudere browser zonder die lijst krijgt een tekstveld. */
function useTimezones(): string[] | null {
  return useMemo(() => {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    try {
      return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : null;
    } catch {
      return null;
    }
  }, []);
}

/**
 * Een vestiging aanmaken of wijzigen, in een paneel rechts (O3).
 *
 * De **code ligt vast na het aanmaken**: hij staat op een scherm aan de muur, in een export en in
 * het hoofd van wie er werkt. Hernoemen kan wel - dat is een naam, geen sleutel.
 */
function SiteDrawer({
  site,
  companyZoneLabel,
  onClose,
  onSaved,
}: {
  site?: Site;
  companyZoneLabel: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const zones = useTimezones();

  const [code, setCode] = useState(site?.code ?? '');
  const [name, setName] = useState(site?.name ?? '');
  const [address, setAddress] = useState(site?.address ?? '');
  const [timezone, setTimezone] = useState(site?.timezone ?? '');
  const [status, setStatus] = useState(site?.status ?? 'active');
  const [fout, setFout] = useState<string | null>(null);

  const bewaar = useMutation({
    mutationFn: async () => {
      if (site) {
        const { data, error } = await api.PATCH('/api/v1/sites/{siteId}', {
          params: { path: { siteId: site.id } },
          body: { name, address, timezone: timezone || null, status } as never,
        });
        if (error || !data) throw error ?? new Error('Opslaan mislukt');
        return;
      }
      const { data, error } = await api.POST('/api/v1/sites', {
        body: { code: code.toUpperCase(), name, address, timezone: timezone || null } as never,
      });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
    },
    onSuccess: () => void onSaved(),
    onError: (error: unknown) => {
      // De server noemt het veld bij een dubbele code; die zin hoort de beheerder te lezen.
      const detail = (error as { detail?: string })?.detail;
      setFout(detail ?? t('sites.failed'));
      toast.show({ title: t('sites.failed'), description: detail, tone: 'danger' });
    },
  });

  const kanBewaren = name.trim().length >= 2 && (site !== undefined || code.trim().length >= 2);
  // Een tijdzone die de browser niet kent (of een oude waarde), blijft kiesbaar.
  const zoneOpties = zones && timezone && !zones.includes(timezone) ? [timezone, ...zones] : zones;

  return (
    <Drawer
      open
      onClose={onClose}
      title={site ? t('sites.edit') : t('sites.new')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            form="vestiging"
            type="submit"
            icon={site ? undefined : <AdminIcon name="plus" />}
            testId="site-save"
            disabled={bewaar.isPending || !kanBewaren}
          >
            {site ? t('common.save') : t('sites.new')}
          </Btn>
        </>
      }
    >
      <Form id="vestiging" onSubmit={() => kanBewaren && bewaar.mutate()}>
        <Row>
          <Field
            label={t('sites.code')}
            hint={
              site
                ? t('beheer.organisatie.sites.codeFixed')
                : t('beheer.organisatie.sites.codeHint')
            }
            error={!site ? (fout ?? undefined) : undefined}
          >
            {(props) => (
              <Input
                {...props}
                data-testid="site-code"
                value={code}
                maxLength={12}
                readOnly={site !== undefined}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label={t('sites.name')}>
            {(props) => (
              <Input
                {...props}
                data-testid="site-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
        </Row>
        <Field label={t('sites.address')} optional>
          {(props) => (
            <Input
              {...props}
              placeholder={t('beheer.organisatie.sites.addressPlaceholder')}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('sites.timezone')} hint={t('beheer.organisatie.sites.timezoneHint')}>
          {(props) =>
            zoneOpties ? (
              <Select
                {...props}
                data-testid="site-timezone"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
              >
                <option value="">{companyZoneLabel}</option>
                {zoneOpties.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                {...props}
                data-testid="site-timezone"
                placeholder={companyZoneLabel}
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
              />
            )
          }
        </Field>
        {site ? (
          <Field label={t('sites.status')}>
            {(props) => (
              <Select
                {...props}
                value={status}
                onChange={(event) => setStatus(event.target.value as 'active' | 'closed')}
              >
                <option value="active">{t('sites.statusValue.active')}</option>
                <option value="closed">{t('sites.statusValue.closed')}</option>
              </Select>
            )}
          </Field>
        ) : null}
        {/* Sluiten en niet verwijderen: een bericht van vorig jaar ging naar deze vestiging. */}
        {site ? <Note>{t('sites.closeHint')}</Note> : null}
      </Form>
    </Drawer>
  );
}
